// The diagram format lives in the link: /{database}/map/{guid}?format=…
// (/{database}/model/{guid} is the same address under its older name, and
// redirects here with its query string intact - App.js.)
//
// A link names the format it was copied from - bpmn, svg, png, mermaid, pdf,
// structure - and opens on it. Switching format in the reader rewrites the link in place
// (history.replace), so going back leaves the diagram rather than stepping
// through the formats. A format the object doesn't have, or a word that isn't
// a format at all, is not an error: the reader opens on what it would have
// chosen anyway.

// The live map of an object's parts, beside its stored diagrams.
export const STRUCTURE = "structure";

// Stored formats are offered in this order - the same order the API offers
// them in (backend/config/diagramFormats.js FORMAT_LABELS); anything else
// follows. A drawing first, then pictures, then a document.
export const FORMAT_ORDER = ["bpmn", "svg", "png", "jpg", "jpeg", "webp", "gif", "mermaid", "pdf"];

// The formats the app draws itself, from what the object actually holds: a
// BPMN is laid out by bpmn-js, a Mermaid source by mermaid. Everything else
// on the list above is a file somebody exported once - it looks however their
// tool looked that day, and no amount of presentation changes what is in it.
//
// This is the rule behind "open the better picture first": not a list of
// types, but whether the object gives the product something to draw.
export const OWN_DRAWING_FORMATS = ["bpmn", "mermaid"];

// The pictures a library stores. An object with one of these and nothing the
// app can draw itself has only somebody's export to show; that is where our
// own drawing takes over (`displaced` below).
export const PICTURE_FORMATS = ["svg", "png", "jpg", "jpeg", "webp", "gif"];

// The name of the secondary choice where our drawing is the object's picture:
// the file exactly as it was imported, with its Enhanced/Original switch and
// its own download. It is a name for a tab and for a link - the format it
// reads is whichever picture the object actually stores (`sourceFormat`).
export const SOURCE = "source";

export const sortFormats = (formats = []) =>
  [...formats].sort((a, b) => {
    const ia = FORMAT_ORDER.indexOf(a);
    const ib = FORMAT_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

// What a link asks for: a format name, without case or surrounding spaces.
export const readFormat = (search) => {
  const raw = new URLSearchParams(typeof search === "string" ? search : "").get("format");
  const wanted = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  return /^[a-z0-9]{2,12}$/.test(wanted) ? wanted : null;
};

// Every way this object can be shown, in the order of the format tabs. Any
// object with parts offers its live map (whether it has parts comes from the
// graph, not its type); `mapFirstType` only puts the map first - and opens on
// it (bestFormat) - while its stored diagrams stay one click away.
// Whether the map comes before this object's stored files. Two ways it can:
// the admin named the type in `diagram.mapFirstTypes`, or - the rule, and
// what `mapBeforeExports` switches - the object gives us nothing to draw
// ourselves, so the only picture it has is somebody's export and the live map
// is the better first look. The export is never taken away, only put second.
export const mapComesFirst = ({ drawn = [], mapFirstType = false, mapBeforeExports = false } = {}) => {
  if (mapFirstType) return true;
  if (!mapBeforeExports) return false;
  return !drawn.some((format) => OWN_DRAWING_FORMATS.includes(format));
};

// The stored picture our own drawing stands in front of: the best one the
// object has, in the order the app offers formats. Null where it stores none.
export const sourceFormat = ({ drawn = [] } = {}) => sortFormats(drawn).find((format) => PICTURE_FORMATS.includes(format)) || null;

// Whether our drawing is this object's picture rather than the file somebody
// exported. Three things have to hold, and none of them is a type: the object
// has parts to draw, it gives the app nothing of its own to draw instead, and
// the database hasn't switched it off (`diagram.mapAsSvg`). The export is
// never taken away - it is downloadable and one link away (`SOURCE`), and an
// admin can put it back among the views (`sourceIsAView`).
//
// The same rule on the server: backend/services/generatedMap.js mapIsThePicture.
export const displaced = ({ drawn = [], hasStructure = false, mapAsSvg = false } = {}) =>
  Boolean(hasStructure) &&
  Boolean(mapAsSvg) &&
  !drawn.some((format) => OWN_DRAWING_FORMATS.includes(format)) &&
  sourceFormat({ drawn }) !== null;

// Whether the product has a picture of this object that is its own work. Two
// ways, and neither of them names a type, a level or a customer:
//
//   - the object holds a model the app lays out - a flow, a Mermaid source;
//   - it has parts, and this database shows them as the object's picture
//     (`diagram.mapAsSvg`).
//
// This is the whole test behind "don't offer somebody's export where we draw
// the object ourselves".
export const drawnByUs = ({ drawn = [], hasStructure = false, mapAsSvg = false } = {}) =>
  drawn.some((format) => OWN_DRAWING_FORMATS.includes(format)) || (Boolean(hasStructure) && Boolean(mapAsSvg));

// Whether an imported picture is one of this object's views at all.
//
// Where we draw the object, it is not: no tab, no toggle, no note - an old
// export beside our own drawing is clutter, and the one the product owner was
// shown is worse than what it sits next to. The file is untouched: it is still
// stored, still in the Files menu, still downloadable under its own name, and
// still reachable by ?format=source. An admin whose exports are good brings it
// back as a view with `diagram.showSourcePicture`.
export const sourceIsAView = (options = {}) => Boolean(options.showSource) || !drawnByUs(options);

export const availableFormats = (options = {}) => {
  const { hasStructure = false, mapFirstType = false, mapBeforeExports = false, mapAsSvg = false } = options;
  const stored = sortFormats(options.drawn);
  const withSource = sourceIsAView({ ...options, drawn: stored });
  // A document (a PDF) is not somebody's picture of this object and keeps its
  // own tab either way; only the pictures step aside.
  const others = withSource ? stored : stored.filter((format) => !PICTURE_FORMATS.includes(format));
  if (!hasStructure) return others;
  if (displaced({ drawn: stored, hasStructure, mapAsSvg })) {
    // Our drawing first - it is the picture - then, where the imported file is
    // offered at all, it under its own name, then anything else the object
    // holds (a PDF keeps its own tab either way).
    if (!withSource) return [STRUCTURE, ...others];
    const source = sourceFormat({ drawn: stored });
    return [STRUCTURE, SOURCE, ...stored.filter((format) => format !== source)];
  }
  return mapComesFirst({ drawn: stored, mapFirstType, mapBeforeExports }) ? [STRUCTURE, ...others] : [...others, STRUCTURE];
};

// The format to open on when the link doesn't say.
export const bestFormat = (options = {}) => availableFormats(options)[0] || null;

// A format a link asks for, as this object shows it. A link written before our
// drawing became the object's picture names the stored format ("?format=svg"):
// it asked for the object's picture, so it still opens the object's picture,
// which is now the one we draw. The imported file itself is "?format=source",
// and every other format is itself.
export const resolveRequested = (wanted, options = {}) => {
  if (!wanted) return null;
  // Only a picture this object actually stores is re-read this way: a format
  // it doesn't have is not a choice at all, and falls back as it always did.
  if (!PICTURE_FORMATS.includes(wanted) || !(options.drawn || []).includes(wanted)) return wanted;
  if (displaced(options) && wanted === sourceFormat(options)) return STRUCTURE;
  if (sourceIsAView(options)) return wanted;
  return bestFormat(options);
};

// The format to open on, and whether the link chose it (a reader's own choice
// is never overruled by the view configuration arriving late).
export const chooseFormat = ({ requested, ...options } = {}) => {
  const asked = typeof requested === "string" ? requested.trim().toLowerCase() : null;
  // "?format=source" names the imported file itself, and keeps working whether
  // or not that file is offered as a view - somebody wrote that link meaning
  // that file. Where our drawing is the object's picture the file has a name
  // of its own; otherwise it is simply the picture the object stores.
  if (asked === SOURCE) {
    const stored = sourceFormat(options);
    if (stored) return { format: displaced(options) ? SOURCE : stored, fromLink: true };
    return { format: bestFormat(options), fromLink: false };
  }
  const wanted = resolveRequested(asked, options);
  const available = availableFormats(options);
  if (wanted && available.includes(wanted)) return { format: wanted, fromLink: true };
  return { format: bestFormat(options), fromLink: false };
};

// The query string this format belongs in, or null when the link already says
// so - so the reader's URL is rewritten only where it actually changes.
export const withFormat = (search, format) => {
  if (!format) return null;
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  if (params.get("format") === format) return null;
  params.set("format", format);
  return `?${params.toString()}`;
};
